/**
 * Piece geometry, as data.
 *
 * Spawn shapes are the ones the reference prototype uses
 * (`reference/mode-demos.html`), which are the standard SRS spawn orientations:
 * J, L, S, T and Z in a 3x3 box, I in a 4x4 box, O in a 3x3 box offset so that
 * it lands on columns 4 and 5 of a 10-wide well.
 *
 * The other three rotation states are derived by rotating the bounding box
 * clockwise, `[x, y] -> [size - 1 - y, x]`, exactly as the prototype derives
 * them. Deriving beats hand-writing four states per piece: there is one rule to
 * get wrong instead of twenty-eight literals.
 *
 * O is the exception. SRS spins it in place, and the prototype refuses the
 * rotation outright; here all four of its states are the spawn state, so a
 * rotation of O can never move a cell no matter who calls it.
 */
import type { PieceKind, RotationDirection, RotationState, Vec2 } from './types.js';

/** Canonical order. This is the order a fresh 7-bag starts in before shuffling. */
export const PIECE_KINDS: readonly PieceKind[] = ['I', 'J', 'L', 'O', 'S', 'T', 'Z'];

/** The four rotation states of one piece, indexed by `RotationState`. */
export type RotationTable = readonly [
  readonly Vec2[],
  readonly Vec2[],
  readonly Vec2[],
  readonly Vec2[],
];

interface ShapeSpec {
  /** Width and height of the rotation box. */
  readonly size: number;
  /** Occupied cells of the spawn state, as `[x, y]` inside the box. */
  readonly cells: readonly Vec2[];
}

const SPAWN_SHAPES: Readonly<Record<PieceKind, ShapeSpec>> = {
  I: {
    size: 4,
    cells: [
      [0, 1],
      [1, 1],
      [2, 1],
      [3, 1],
    ],
  },
  J: {
    size: 3,
    cells: [
      [0, 0],
      [0, 1],
      [1, 1],
      [2, 1],
    ],
  },
  L: {
    size: 3,
    cells: [
      [2, 0],
      [0, 1],
      [1, 1],
      [2, 1],
    ],
  },
  O: {
    size: 3,
    cells: [
      [1, 0],
      [2, 0],
      [1, 1],
      [2, 1],
    ],
  },
  S: {
    size: 3,
    cells: [
      [1, 0],
      [2, 0],
      [0, 1],
      [1, 1],
    ],
  },
  T: {
    size: 3,
    cells: [
      [1, 0],
      [0, 1],
      [1, 1],
      [2, 1],
    ],
  },
  Z: {
    size: 3,
    cells: [
      [0, 0],
      [1, 0],
      [1, 1],
      [2, 1],
    ],
  },
};

/** Sort into reading order so the data is stable and easy to eyeball in a test. */
function canonical(cells: readonly Vec2[]): readonly Vec2[] {
  return [...cells].sort((a, b) => (a[1] === b[1] ? a[0] - b[0] : a[1] - b[1]));
}

/** One clockwise quarter turn inside a `size` x `size` box. */
function rotateCells(cells: readonly Vec2[], size: number): readonly Vec2[] {
  return canonical(cells.map(([x, y]): Vec2 => [size - 1 - y, x]));
}

function buildRotations(kind: PieceKind): RotationTable {
  const spec = SPAWN_SHAPES[kind];
  const spawn = canonical(spec.cells);
  if (kind === 'O') {
    // O never turns. Four identical states make that true by construction.
    return [spawn, spawn, spawn, spawn];
  }
  const cw = rotateCells(spawn, spec.size);
  const flip = rotateCells(cw, spec.size);
  const ccw = rotateCells(flip, spec.size);
  return [spawn, cw, flip, ccw];
}

/** `ROTATIONS[kind][rotation]` is the occupied box cells of that state. */
export const ROTATIONS: Readonly<Record<PieceKind, RotationTable>> = {
  I: buildRotations('I'),
  J: buildRotations('J'),
  L: buildRotations('L'),
  O: buildRotations('O'),
  S: buildRotations('S'),
  T: buildRotations('T'),
  Z: buildRotations('Z'),
};

/** Size of a piece's rotation box: 4 for I, 3 for everything else. */
export function boxSize(kind: PieceKind): number {
  return SPAWN_SHAPES[kind].size;
}

/** Occupied box cells for one rotation state. Does not allocate. */
export function pieceCells(kind: PieceKind, rotation: RotationState): readonly Vec2[] {
  return ROTATIONS[kind][rotation];
}

/** Occupied board cells for a piece placed at `(x, y)`. Allocates. */
export function pieceCellsAt(
  kind: PieceKind,
  rotation: RotationState,
  x: number,
  y: number,
): readonly Vec2[] {
  return pieceCells(kind, rotation).map(([cx, cy]): Vec2 => [x + cx, y + cy]);
}

/** Turn a rotation state by one step. `1` is clockwise, `-1` counter-clockwise. */
export function rotateState(from: RotationState, direction: RotationDirection): RotationState {
  return ((from + (direction > 0 ? 1 : 3)) % 4) as RotationState;
}
